import React, {useState} from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import PrintoutDocumentRenderer from "../../PrintoutDocumentRenderer";
import PrintPreviewPageShell from "../../shell/PrintPreviewPageShell";
import PrintPreviewPrintButton from "../../shell/PrintPreviewPrintButton";
import PrintPreviewSidebarContent from "../../shell/PrintPreviewSidebarContent";
import {
    getPrintoutLayoutOptions,
    getPrintoutLayoutSelectionValue,
    parsePrintoutLayoutSelection,
    type PrintoutLayoutSelection,
} from "../../shell/printoutLayoutSelection";
import {usePrintoutDocumentVariables} from "../../shell/printoutDocumentVariables";
import {
    EMPTY_PRINT_VALUE,
    formatPrintNativeLanguage,
    formatPrintStatus,
    formatPrintText,
} from "../../shell/printPreviewFormatters";
import {resolvePrintoutVariable} from "../../../variables/printoutVariableResolver";
import {useFolioPrintData} from "./useFolioPrintData";

const getPrintoutTypeLabel = (type?: Printout["type"]) => {
    switch (type) {
        case PrintoutType.FOLIO_CONFIRMATION:
            return "Folio confirmation";
        default:
            return formatPrintStatus(type);
    }
};

const FolioPrintPage = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const [selectedLayout, setSelectedLayout] = useState<PrintoutLayoutSelection | undefined>();
    const printData = useFolioPrintData(id, selectedLayout?.printoutId, selectedLayout?.language);

    const selectedPrintout = printData.selectedPrintout;
    const folio = printData.folio;
    const printLanguage = printData.language;
    const selectedPrintoutLanguage = selectedPrintout
        ? formatPrintNativeLanguage(printLanguage)
        : undefined;
    const documentVariables = usePrintoutDocumentVariables(selectedPrintout, printData.context, printLanguage);

    const billToName = [
        folio?.billTo?.firstName,
        folio?.billTo?.lastName,
    ].filter(Boolean).join(" ").trim();
    const profileName = [
        printData.profile?.firstName,
        printData.profile?.lastName,
    ].filter(Boolean).join(" ").trim();
    const profileEmail = printData.profile?.emails?.find(email => !email.hidden && email.isPrimary)?.email
        ?? printData.profile?.emails?.find(email => !email.hidden)?.email;
    const profilePhone = printData.profile?.phones?.find(phone => !phone.hidden && phone.isPrimary)?.phoneNumberFull
        ?? printData.profile?.phones?.find(phone => !phone.hidden)?.phoneNumberFull;
    const guestLanguage = printData.profile?.languageCode;
    const currency = folio?.currency ?? folio?.total?.currency;
    const resolvedValue = (key: string) => (
        printData.context
            ? resolvePrintoutVariable(key, printData.context)?.displayValue || EMPTY_PRINT_VALUE
            : EMPTY_PRINT_VALUE
    );

    const folioRows = [
        {label: t("Name"), value: formatPrintText(folio?.name)},
        {label: t("Status"), value: formatPrintStatus(folio?.status)},
        {label: t("Currency"), value: formatPrintText(currency)},
        {label: t("Items"), value: String(printData.folioItems.length)},
    ];

    const billingRows = [
        {label: t("Recipient"), value: formatPrintText(billToName || profileName)},
        {label: t("Email"), value: formatPrintText(folio?.billTo?.email || profileEmail)},
        {label: t("Phone"), value: formatPrintText(folio?.billTo?.phone || profilePhone)},
    ];

    const amountRows = [
        {label: t("Total"), value: resolvedValue("folioTotal")},
        {label: t("Paid"), value: resolvedValue("folioAmountPaid")},
        {label: t("Due"), value: resolvedValue("folioAmountDue")},
        {label: t("Remaining"), value: resolvedValue("folioAmountRemaining")},
    ];

    const closePreview = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        window.close();
    };

    return (
        <PrintPreviewPageShell
            actions={(
                <PrintPreviewPrintButton
                    disabled={!selectedPrintout || !printData.context}
                    onClick={() => window.print()}
                />
            )}
            sidebar={(
                <PrintPreviewSidebarContent
                    alert={guestLanguage ? (
                        <>
                            {t("Guest language")}: <strong>{formatPrintNativeLanguage(guestLanguage)}</strong>
                        </>
                    ) : undefined}
                    onClose={closePreview}
                    selects={[
                        {
                            id: "folio-printout-layout",
                            disabled: printData.isLoading || !printData.printouts.length,
                            emptyLabel: t("No folio printouts"),
                            label: t("Printout"),
                            onChange: value => setSelectedLayout(parsePrintoutLayoutSelection(value)),
                            options: getPrintoutLayoutOptions(
                                printData.printouts,
                                formatPrintNativeLanguage,
                                t("Untitled printout"),
                            ),
                            value: getPrintoutLayoutSelectionValue(selectedPrintout, printLanguage),
                        },
                    ]}
                    tables={[
                        {rows: folioRows},
                        {rows: billingRows},
                        {rows: amountRows},
                    ]}
                    printoutInfo={selectedPrintout ? {
                        language: selectedPrintoutLanguage,
                        type: t(getPrintoutTypeLabel(selectedPrintout.type)),
                    } : undefined}
                    title={t("Folio Print")}
                    variables={documentVariables}
                />
            )}
        >
            {printData.isLoading && (
                <Stack alignItems="center" justifyContent="center" minHeight="100vh" spacing={2}>
                    <CircularProgress/>
                    <Typography color="text.secondary">
                        {t("Loading folio print preview")}
                    </Typography>
                </Stack>
            )}

            {!printData.isLoading && printData.errorMessage && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert severity="error">
                        {printData.errorMessage}
                    </Alert>
                </Box>
            )}

            {!printData.isLoading && !printData.errorMessage && !selectedPrintout && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert severity="info">
                        {t("No folio printout was found.")}
                    </Alert>
                </Box>
            )}

            {!printData.isLoading && selectedPrintout && printData.context && (
                <PrintoutDocumentRenderer
                    context={printData.context}
                    language={printLanguage}
                    printout={selectedPrintout}
                />
            )}
        </PrintPreviewPageShell>
    );
};

export default FolioPrintPage;
